import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ChevronLeft, 
  BookOpen, 
  Eye,
  ShoppingCart,
  Check,
  ChevronRight,
  Star,
  Truck,
  CreditCard
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { getPortalSession } from '../lib/familyPortalSession';
import { functions } from '../lib/firebase';

const BOOK_TIERS = [
  {
    id: 'mini',
    name: 'Mini Keepsake',
    pages: 12,
    price: 29,
    maxPhotos: 4,
    description: 'Perfect memento with key highlights'
  },
  {
    id: 'standard',
    name: 'Standard Book',
    pages: 32,
    price: 79,
    maxPhotos: 12,
    description: 'Comprehensive story with photos',
    popular: true
  },
  {
    id: 'deluxe',
    name: 'Deluxe Edition',
    pages: 48,
    price: 129,
    maxPhotos: 20,
    description: 'Complete life story with all details'
  }
];

export default function FamilyBookView() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const startWithOrder = searchParams.get('order') === 'true';
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [story, setStory] = useState(null);
  const [portal, setPortal] = useState(null);
  
  // Order flow state
  const [step, setStep] = useState(startWithOrder ? 'select' : 'preview'); // 'preview', 'select', 'customize', 'checkout'
  const [selectedTier, setSelectedTier] = useState('standard');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [processingCheckout, setProcessingCheckout] = useState(false);
  
  // Customization
  const [colorScheme, setColorScheme] = useState('classic');
  const [dedication, setDedication] = useState('');

  // Cloud Functions
  const loadFamilyPortalFn = httpsCallable(functions, 'loadFamilyPortal');

  useEffect(() => {
    loadData();
  }, [token]);

  const loadData = async () => {
    try {
      // =============================================
      // SECURITY FIX: Use Cloud Function instead of
      // direct Firestore reads. Family members are
      // unauthenticated — Firestore rules block
      // direct reads on lifeStories.
      // =============================================
      const sessionToken = getPortalSession(token);
      const result = await loadFamilyPortalFn({ portalToken: token, sessionToken });

      // PIN-protected portal without a valid session — send the visitor to the
      // main portal page to enter the PIN.
      if (result.data?.requiresPin) {
        navigate(`/family/${token}`);
        return;
      }

      if (!result.data.success) {
        setError('Failed to load book information.');
        setLoading(false);
        return;
      }

      const { portal: portalData, story: storyData } = result.data;
      setPortal(portalData);
      
      // Check permissions
      if (!portalData.permissions?.viewBookPreview && !portalData.permissions?.canOrderBook) {
        setError('You do not have permission to view or order books.');
        setLoading(false);
        return;
      }
      
      setStory(storyData);
      setLoading(false);
    } catch (err) {
      console.error('Error loading data:', err);
      if (err.code === 'not-found') {
        setError('Invalid or expired link.');
      } else {
        setError('Failed to load book information.');
      }
      setLoading(false);
    }
  };

  const generatePreview = async () => {
    setGeneratingPreview(true);
    try {
      const generateBookPreview = httpsCallable(functions, 'generateBookPreviewPublic');
      const result = await generateBookPreview({
        storyId: story.id,
        tier: selectedTier,
        familyPortalToken: token
      });
      
      setPreviewUrl(result.data.previewUrl);
    } catch (err) {
      console.error('Error generating preview:', err);
      alert('Failed to generate preview. Please try again.');
    }
    setGeneratingPreview(false);
  };

  const handleCheckout = async () => {
    setProcessingCheckout(true);
    try {
      const createFamilyCheckout = httpsCallable(functions, 'createFamilyCheckoutSession');
      const result = await createFamilyCheckout({
        storyId: story.id,
        tier: selectedTier,
        colorScheme,
        dedication,
        familyPortalToken: token
      });
      
      // Redirect to Stripe checkout
      window.location.href = result.data.checkoutUrl;
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Failed to start checkout. Please try again.');
      setProcessingCheckout(false);
    }
  };

  const getDisplayName = () => {
    if (!story) return '';
    const name = story.subjectName || story.preferredName || story.residentName || '';
    if (story.coupleMode && story.partnerName) {
      return `${name} & ${story.partnerName}`;
    }
    return name;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-quiltt-purple border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate(`/family/${token}`)}
            className="px-6 py-2 bg-quiltt-purple text-white rounded-lg"
          >
            Back to Portal
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                if (step === 'preview') {
                  navigate(`/family/${token}`);
                } else if (step === 'select') {
                  setStep('preview');
                } else if (step === 'customize') {
                  setStep('select');
                }
              }}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex-1">
              <h1 className="font-bold text-gray-900">
                {step === 'preview' ? 'Preview Book' : 
                 step === 'select' ? 'Select Book' : 
                 'Customize & Order'}
              </h1>
              <p className="text-sm text-gray-500">{getDisplayName()}'s Life Story</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Preview Step */}
        {step === 'preview' && (
          <div className="space-y-6">
            {/* Book mockup */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex flex-col md:flex-row items-center gap-8">
                {/* Book image */}
                <div className="w-48 h-64 bg-gradient-to-br from-quiltt-purple to-purple-700 rounded-lg shadow-xl flex items-center justify-center">
                  <div className="text-center text-white p-4">
                    <BookOpen className="w-12 h-12 mx-auto mb-2" />
                    <p className="font-bold">{getDisplayName()}</p>
                    <p className="text-sm text-white/80">A Life Story</p>
                  </div>
                </div>
                
                {/* Info */}
                <div className="flex-1 text-center md:text-left">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {getDisplayName()}'s Life Story
                  </h2>
                  <p className="text-gray-600 mb-4">
                    A beautiful printed keepsake capturing precious memories, 
                    stories, and photos to treasure for generations.
                  </p>
                  <div className="flex flex-wrap gap-4 justify-center md:justify-start text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Check className="w-4 h-4 text-green-500" />
                      Professional printing
                    </span>
                    <span className="flex items-center gap-1">
                      <Truck className="w-4 h-4 text-green-500" />
                      Free shipping
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Preview button */}
            {portal?.permissions?.viewBookPreview && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Eye className="w-5 h-5 text-quiltt-purple" />
                  Preview the Book
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  See a watermarked preview of how the printed book will look.
                </p>
                {previewUrl ? (
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-medium transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    View Preview
                  </a>
                ) : (
                  <button
                    onClick={generatePreview}
                    disabled={generatingPreview}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-medium transition-colors disabled:opacity-50"
                  >
                    {generatingPreview ? (
                      <>
                        <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4" />
                        Generate Preview
                      </>
                    )}
                  </button>
                )}
              </div>
            )}

            {/* Order button */}
            {portal?.permissions?.canOrderBook && (
              <button
                onClick={() => setStep('select')}
                className="w-full py-4 bg-quiltt-purple hover:bg-purple-700 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors"
              >
                <ShoppingCart className="w-5 h-5" />
                Order a Printed Book
                <ChevronRight className="w-5 h-5" />
              </button>
            )}
          </div>
        )}

        {/* Select Tier Step */}
        {step === 'select' && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose Your Book</h2>
              <p className="text-gray-600">Select the perfect size for {getDisplayName()}'s story</p>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              {BOOK_TIERS.map(tier => (
                <button
                  key={tier.id}
                  onClick={() => setSelectedTier(tier.id)}
                  className={`relative bg-white rounded-xl p-6 text-left transition-all ${
                    selectedTier === tier.id
                      ? 'ring-2 ring-quiltt-purple shadow-lg'
                      : 'border border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {tier.popular && (
                    <span className="absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-1 bg-quiltt-purple text-white text-xs font-bold rounded-full">
                      MOST POPULAR
                    </span>
                  )}
                  
                  <h3 className="font-bold text-gray-900 mb-1">{tier.name}</h3>
                  <p className="text-sm text-gray-500 mb-3">{tier.description}</p>
                  
                  <div className="mb-3">
                    <span className="text-3xl font-bold text-gray-900">${tier.price}</span>
                  </div>
                  
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      {tier.pages} pages
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      Up to {tier.maxPhotos} photos
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      Free shipping
                    </li>
                  </ul>

                  {selectedTier === tier.id && (
                    <div className="absolute top-4 right-4 w-6 h-6 bg-quiltt-purple rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>

            <button
              onClick={() => setStep('customize')}
              className="w-full py-4 bg-quiltt-purple hover:bg-purple-700 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors"
            >
              Continue to Customize
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Customize & Checkout Step */}
        {step === 'customize' && (
          <div className="space-y-6">
            {/* Order summary */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Order Summary</h3>
              <div className="flex items-center justify-between pb-4 border-b">
                <div>
                  <p className="font-medium text-gray-900">
                    {BOOK_TIERS.find(t => t.id === selectedTier)?.name}
                  </p>
                  <p className="text-sm text-gray-500">
                    {BOOK_TIERS.find(t => t.id === selectedTier)?.pages} pages
                  </p>
                </div>
                <p className="text-xl font-bold text-gray-900">
                  ${BOOK_TIERS.find(t => t.id === selectedTier)?.price}
                </p>
              </div>
              <div className="flex items-center justify-between pt-4 text-sm text-gray-500">
                <span>Shipping</span>
                <span className="text-green-600 font-medium">FREE</span>
              </div>
            </div>

            {/* Color scheme */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Color Scheme</h3>
              <div className="grid grid-cols-5 gap-3">
                {[
                  { id: 'classic', name: 'Classic', colors: ['#4a5568', '#718096'] },
                  { id: 'warm', name: 'Warm Sepia', colors: ['#92400e', '#b45309'] },
                  { id: 'navy', name: 'Navy & Gold', colors: ['#1e3a5f', '#d4af37'] },
                  { id: 'forest', name: 'Forest', colors: ['#065f46', '#047857'] },
                  { id: 'burgundy', name: 'Burgundy', colors: ['#7f1d1d', '#991b1b'] }
                ].map(scheme => (
                  <button
                    key={scheme.id}
                    onClick={() => setColorScheme(scheme.id)}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      colorScheme === scheme.id 
                        ? 'border-quiltt-purple' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex gap-1 mb-2">
                      {scheme.colors.map((color, i) => (
                        <div
                          key={i}
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-gray-600">{scheme.name}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Dedication */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-semibold text-gray-900 mb-2">Personal Dedication (Optional)</h3>
              <p className="text-sm text-gray-500 mb-4">
                Add a personal message that will appear on the first page of the book.
              </p>
              <textarea
                value={dedication}
                onChange={(e) => setDedication(e.target.value)}
                placeholder="To Mom, with love from your family..."
                maxLength={200}
                rows={3}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-quiltt-purple focus:border-transparent"
              />
              <p className="text-xs text-gray-400 mt-1">{dedication.length}/200 characters</p>
            </div>

            {/* Checkout button */}
            <button
              onClick={handleCheckout}
              disabled={processingCheckout}
              className="w-full py-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            >
              {processingCheckout ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5" />
                  Proceed to Checkout - ${BOOK_TIERS.find(t => t.id === selectedTier)?.price}
                </>
              )}
            </button>

            <p className="text-center text-sm text-gray-500">
              Secure checkout powered by Stripe. Ships in 7-10 business days.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
